import React, { useState, useEffect, useCallback } from 'react';

import { Box, Typography, CircularProgress, TextField, Button, Stack, ButtonBase } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { useAppContext } from '@/context';
import { BffBrandType } from '@/types/generated';
import TAGLink from '@/components/TAGLink';
import Drawer from '@/components/Drawer';
import { logError } from '@/utils/miscUtils';
import { getBffNearByFacilitiesByLocation } from '@/services/getBffNearByFacilitiesByLocation';

import {
	drawerContentStyles,
	facilityCardStyles,
	facilityDistanceStyles,
	searchButtonStyles,
} from './SchedulingWidget.styles';
import { FacilitySearchResult, GeocodingResponse } from './SchedulingWidget.types';
import { isValidZipCode, formatFacilityAddress } from './SchedulingWidget.helpers';

interface FetchFacilitiesParams {
	latitude: number;
	longitude: number;
	bffGraphqlUrl: string;
	brand: BffBrandType;
	appName: string;
	appVersion: string;
}

type FetchFacilitiesResult = { facilities: FacilitySearchResult[] } | { error: string };

async function fetchFacilitiesByCoords({
	latitude,
	longitude,
	bffGraphqlUrl,
	brand,
	appName,
	appVersion,
}: FetchFacilitiesParams): Promise<FetchFacilitiesResult> {
	const response = await getBffNearByFacilitiesByLocation(
		brand,
		latitude,
		longitude,
		bffGraphqlUrl,
		appName,
		appVersion,
		{ radiusMiles: 200, maxItems: 10, fetchAllIfNoResults: false }
	);

	if (response === null) {
		return { error: 'Failed to search facilities. Please try again.' };
	}

	const facilities = response?.facilityByLocation?.filter((f) => f !== null) || [];
	return {
		facilities: facilities.map((f) => ({
			id: f?.code || '',
			name: f?.displayName || f?.name || '',
			address: f?.address?.address1 || '',
			city: f?.address?.city || '',
			state: f?.address?.stateCode || '',
			zipCode: f?.address?.zipCode || '',
			distance: f?.location?.distance ?? undefined,
			phoneNumber: f?.phoneNumber ?? undefined,
		})),
	};
}

export interface LocationSearchDrawerProps {
	open: boolean;
	onClose: () => void;
	onSelectFacility: (facility: FacilitySearchResult) => void;
	currentFacilityId?: string;
	initialFacilities?: FacilitySearchResult[];
	brand: BffBrandType;
}

export default function LocationSearchDrawer({
	open,
	onClose,
	onSelectFacility,
	currentFacilityId: _currentFacilityId,
	initialFacilities = [],
	brand,
}: LocationSearchDrawerProps) {
	const { environment, config, appName, appVersion } = useAppContext();
	const [zipCode, setZipCode] = useState('');
	const [searchResults, setSearchResults] = useState<FacilitySearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchError, setSearchError] = useState('');

	// Use initial facilities (from ZIP search) when drawer opens
	useEffect(() => {
		if (open && initialFacilities.length > 0 && searchResults.length === 0) {
			setSearchResults(initialFacilities);
		}
	}, [open, initialFacilities, searchResults.length]);

	const searchAndSetFacilities = useCallback(
		async (latitude: number, longitude: number, noFacilitiesMsg: string) => {
			const bffGraphqlUrl =
				environment === 'prod'
					? config.services.prod.BEFFE_GRAPHQL_URL
					: config.services.nonprod.BEFFE_GRAPHQL_URL;

			if (!bffGraphqlUrl) {
				setSearchError('API configuration error');
				return;
			}

			const result = await fetchFacilitiesByCoords({
				latitude,
				longitude,
				bffGraphqlUrl,
				brand,
				appName: appName || 'shared-components-web',
				appVersion: appVersion || '',
			});

			if ('error' in result) {
				setSearchError(result.error);
				return;
			}

			if (result.facilities.length > 0) {
				setSearchResults(result.facilities);
			} else {
				setSearchError(noFacilitiesMsg);
			}
		},
		[environment, config, brand, appName, appVersion]
	);

	const handleSearch = useCallback(async () => {
		if (!isValidZipCode(zipCode)) {
			setSearchError('Please enter a valid zip code');
			return;
		}

		setIsSearching(true);
		setSearchError('');

		try {
			const geocodeResponse = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
			if (!geocodeResponse.ok) {
				setSearchError('Invalid ZIP code');
				return;
			}

			const geocodeData = (await geocodeResponse.json()) as GeocodingResponse;
			const latitude = Number.parseFloat(geocodeData.places[0].latitude);
			const longitude = Number.parseFloat(geocodeData.places[0].longitude);

			await searchAndSetFacilities(latitude, longitude, 'No facilities found near this location');
		} catch (error) {
			logError(error instanceof Error ? error.message : String(error));
			setSearchError('Failed to search facilities. Please try again.');
		} finally {
			setIsSearching(false);
		}
	}, [zipCode, searchAndSetFacilities]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			void handleSearch();
		}
	};

	const handleUseCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) {
			setSearchError('Geolocation is not supported by your browser');
			return;
		}

		setIsSearching(true);
		setSearchError('');

		const onPosition = async (position: GeolocationPosition) => {
			const { latitude, longitude } = position.coords;
			try {
				await searchAndSetFacilities(latitude, longitude, 'No facilities found near your location');
			} catch (error) {
				logError(error instanceof Error ? error.message : String(error));
				setSearchError('Failed to search facilities. Please try again.');
			} finally {
				setIsSearching(false);
			}
		};

		navigator.geolocation.getCurrentPosition(
			(position) => void onPosition(position),
			() => {
				setSearchError('Unable to retrieve your location. Please enter a ZIP code.');
				setIsSearching(false);
			}
		);
	}, [searchAndSetFacilities]);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			data-testid="location-search-drawer"
			PaperProps={{
				sx: {
					borderTopLeftRadius: '12px',
					borderBottomLeftRadius: '12px',
					width: { sm: '658px', xs: '95%' },
				},
			}}
		>
			<Stack sx={drawerContentStyles} data-testid="location-search-content">
				<Stack direction="row" justifyContent="space-between" mb={1.5}>
					<Typography
						variant="header3"
						data-testid="location-search-title"
						sx={{ fontFamily: 'Beausite Classic' }}
					>
						Find an office
					</Typography>
					<ButtonBase onClick={onClose} data-testid="location-search-close-button">
						<CloseIcon />
					</ButtonBase>
				</Stack>
				<Typography
					variant="bodyMediumBook"
					data-testid="location-search-subtitle"
					sx={{ fontFamily: 'Beausite Classic' }}
				>
					Enter your zip code to find nearby centers
				</Typography>

				<Stack direction="row" spacing={3} mt={1} maxWidth="350px" alignItems="center" mb={1}>
					<TextField
						label="Zip Code"
						value={zipCode}
						onChange={(e) => setZipCode(e.target.value)}
						onKeyPress={handleKeyPress}
						error={!!searchError}
						helperText={searchError}
						disabled={isSearching}
						inputProps={{
							inputMode: 'numeric',
							pattern: '[0-9]*',
							maxLength: 5,
						}}
						data-testid="zip-code-input-drawer"
						sx={{
							'& .MuiInputLabel-root': { fontFamily: 'Beausite Classic' },
							'& .MuiInputBase-input': { fontFamily: 'Beausite Classic' },
							'& .MuiFormHelperText-root': { fontFamily: 'Beausite Classic' },
						}}
					/>
					<Button
						type="submit"
						onClick={() => void handleSearch()}
						disabled={isSearching || !zipCode}
						sx={searchButtonStyles}
						data-testid="search-button-drawer"
					>
						{isSearching ? <CircularProgress size={24} /> : 'Search'}
					</Button>
				</Stack>

				<ButtonBase
					onClick={handleUseCurrentLocation}
					disabled={isSearching}
					data-testid="use-current-location-button"
					sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 3, color: 'text.primary' }}
				>
					<MyLocationIcon sx={{ fontSize: '1.125rem' }} />
					<Typography variant="bodyMediumBook" sx={{ fontFamily: 'Beausite Classic' }}>
						Use my current location
					</Typography>
				</ButtonBase>

				{searchResults.map((facility, index) => (
					<Box key={facility.id} data-testid={`location-facility-${index}`} sx={facilityCardStyles}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
							<Typography
								variant="bodyMediumSemiBold"
								sx={{ color: 'text.primary', fontFamily: 'Beausite Classic' }}
							>
								ClearChoice {facility.city}
							</Typography>
							{facility.distance && (
								<Typography sx={{ ...facilityDistanceStyles, fontFamily: 'Beausite Classic' }}>
									{facility.distance.toFixed(1)} mi
								</Typography>
							)}
						</Stack>
						<Stack spacing={2}>
							<Box>
								<Typography
									variant="bodySmallBook"
									component="a"
									href={`https://www.google.com/maps?q=${encodeURIComponent(
										formatFacilityAddress(facility)
									)}`}
									target="_blank"
									rel="noopener noreferrer"
									sx={{ color: 'text.interactive', mb: 2, fontFamily: 'Beausite Classic' }}
								>
									{formatFacilityAddress(facility)}
								</Typography>
								{facility.phoneNumber && (
									<Typography
										variant="bodySmallBook"
										sx={{
											color: 'text.interactive',
											display: 'block',
											mt: 0.5,
											fontFamily: 'Beausite Classic',
										}}
									>
										<TAGLink
											href={`tel:${facility.phoneNumber}`}
											linkSx={{ color: 'text.interactive' }}
										>
											{facility.phoneNumber}
										</TAGLink>
									</Typography>
								)}
							</Box>
							<Button
								variant="primaryHC"
								size="S"
								sx={{ border: '1px solid', width: 'max-content' }}
								onClick={() => {
									onSelectFacility(facility);
									onClose();
								}}
								data-testid={`select-center-${index}`}
							>
								Select Center
							</Button>
						</Stack>
					</Box>
				))}
			</Stack>
		</Drawer>
	);
}
